import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { PessoaDataService } from '@nomoh-monorepo/data-access-shared-services';
import {
  APP_API_NAME,
  EnvironmentService,
} from '@nomoh-monorepo/shared-data-access-environment';
import {
  IAuthErrorResponse,
  IBardoLoginMultiContexto,
  IContextoBardo,
  isValidSistemaEnum,
  ITermoPendente,
  PerfilAcessoBardo,
  SistemaEnum,
} from '@nomoh-monorepo/shared-types';
import { firstValueFrom, Observable } from 'rxjs';
import { ILoginCredentials, IUsuario, LoginResult } from '../models/auth.interface';
import { POST_LOGOUT_ROUTE } from '../tokens/auth-redirect.token';
import { estaNaRotaMinhaConta } from '../utils/minha-conta-retorno.util';
import { PermissionValidatorService } from './permission-validator.service';

export interface LoginRequest extends ILoginCredentials {
  sistema: SistemaEnum; // ✅ Código do sistema para validação de acesso
}

@Injectable({ providedIn: 'root' })
export class AuthDataService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private environment = inject(EnvironmentService);
  private appApiName = inject(APP_API_NAME); // ✅ Nome da API do app
  private pessoaDataService = inject(PessoaDataService);
  private permissionService = inject(PermissionValidatorService);
  private postLogoutRoute = inject(POST_LOGOUT_ROUTE);

  // Estado com Signals + localStorage sync
  private _user = signal<IUsuario | null>(this.getUserFromStorage());
  private _isAuthenticated = signal<boolean>(this.getAuthStateFromStorage());
  private _loading = signal<boolean>(false);
  private _error = signal<string | null>(null);
  private _sessionVerified = signal<boolean>(false);
  private _termosPendentes = signal<ITermoPendente[]>([]);
  private _pendingContextosBardo = signal<IContextoBardo[]>([]);
  private _bardoContextoAtivo = signal<IContextoBardo | null>(null);
  private _completarCadastroPendente = signal(false);

  // Signals públicos (readonly)
  $user = this._user.asReadonly();
  $isAuthenticated = this._isAuthenticated.asReadonly();
  $loading = this._loading.asReadonly();
  $error = this._error.asReadonly();
  $sessionVerified = this._sessionVerified.asReadonly();
  $termosPendentes = this._termosPendentes.asReadonly();
  $pendingContextosBardo = this._pendingContextosBardo.asReadonly();
  $bardoContextoAtivo = this._bardoContextoAtivo.asReadonly();
  $completarCadastroPendente = this._completarCadastroPendente.asReadonly();

  setTermosPendentes(termos: ITermoPendente[]): void {
    this._termosPendentes.set(termos);
  }

  clearCompletarCadastroPendente(): void {
    this._completarCadastroPendente.set(false);
  }

  /** Sempre a API do app (main.ts). Não mutar — evita OAuth/cookies em porta errada. */
  private get baseUrl(): string {
    return `${this.environment.getApiUrl(this.appApiName)}/auth`;
  }

  private avaliarCompletarCadastro(user: IUsuario): void {
    const quantidade = user.quantidadeLogins ?? 0;
    if (quantidade >= 2 && user.perfilCadastroIncompleto) {
      this._completarCadastroPendente.set(true);
    } else {
      this._completarCadastroPendente.set(false);
    }
  }

  private usuarioComPerfilCadastro(
    base: {
      id: string;
      email?: string | null;
      name?: string;
      nome?: string;
      sistemaId?: string;
      sistemaNome?: string;
      planoExpirado?: boolean;
      planoCancelado?: boolean;
    },
    perfil: {
      quantidadeLogins?: number;
      perfilCadastroIncompleto?: boolean;
      termoPendente?: boolean;
      termos?: ITermoPendente[];
      sistemaId?: string;
      sistemaNome?: string;
      planoExpirado?: boolean;
      planoCancelado?: boolean;
    }
  ): IUsuario {
    return {
      id: base.id,
      email: base.email ?? '',
      name: base.name ?? base.nome ?? '',
      quantidadeLogins: perfil.quantidadeLogins,
      perfilCadastroIncompleto: perfil.perfilCadastroIncompleto,
      sistemaId: base.sistemaId ?? perfil.sistemaId,
      sistemaNome: base.sistemaNome ?? perfil.sistemaNome,
      planoExpirado: base.planoExpirado ?? perfil.planoExpirado,
      planoCancelado: base.planoCancelado ?? perfil.planoCancelado,
      termoPendente: perfil.termoPendente,
      termos: perfil.termos,
    };
  }

  redirecionarSePlanoBloqueado(user: IUsuario | null = this._user()): boolean {
    if (!user?.id || (!user.planoExpirado && !user.planoCancelado)) {
      return false;
    }

    if (estaNaRotaMinhaConta()) {
      return false;
    }

    const origin = window.location.origin;
    const params = new URLSearchParams({
      avisoExpirado: user.planoExpirado ? 'true' : 'false',
      avisoCancelado: user.planoCancelado ? 'true' : 'false',
      sistemaId: user.sistemaId ?? '',
      sistemaNome: user.sistemaNome ?? '',
    });
    window.location.href = `${origin}/minha-conta/${user.id}?${params.toString()}`;
    return true;
  }

  async marcarPlanoAtivoAposPagamento(): Promise<void> {
    this.permissionService.clearPermissions();
    const renovado = await this.refreshToken();
    if (!renovado) {
      const user = this._user();
      if (user) {
        this.setUser(
          { ...user, planoExpirado: false, planoCancelado: false },
          this.resolveSistemaAtivo() ?? undefined
        );
      }
    }
    if (!this.permissionService.$permissionsLoaded()) {
      const sistema = this.resolveSistemaAtivo();
      if (sistema) {
        await this.permissionService.loadPermissionsForSystem(sistema);
      }
    }
  }

  private navegarSemPlano(errorBody?: IAuthErrorResponse): boolean {
    if (
      errorBody?.code !== 'NO_PLAN' &&
      errorBody?.code !== 'PLAN_EXPIRED' &&
      errorBody?.code !== 'PLAN_CANCELLED'
    ) {
      return false;
    }

    if (estaNaRotaMinhaConta()) {
      return false;
    }

    this.router.navigate(['/plano-expirado'], {
      queryParams: {
        sistema: errorBody.sistemaNome,
        sistemaId: errorBody.sistemaId,
      },
    });
    return true;
  }

  private sincronizarCompletarCadastro(user: IUsuario): void {
    const atual = this._user();
    const merged: IUsuario = {
      ...(atual ?? {}),
      ...user,
      id: user.id,
      email: user.email ?? atual?.email ?? '',
      name: user.name ?? atual?.name ?? '',
    };
    this.setUser(merged, this.getSistemaFromStorage() ?? SistemaEnum.Bardo);
    this.avaliarCompletarCadastro(merged);
  }

  removeTermoAceito(termoId: string): void {
    this._termosPendentes.update((curr) =>
      curr.filter((t) => t.termoId !== termoId)
    );
  }

  // Métodos de localStorage sync
  private getUserFromStorage(): IUsuario | null {
    try {
      const stored = localStorage.getItem('nomoh_user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }

  private getAuthStateFromStorage(): boolean {
    return localStorage.getItem('nomoh_auth') === 'true';
  }

  private getSistemaFromStorage(): SistemaEnum | null {
    try {
      const stored = localStorage.getItem('nomoh_sistema_id');
      if (!stored) return null;
      return isValidSistemaEnum(stored) ? stored : null;
    } catch {
      return null;
    }
  }

  /**
   * Resolve o sistema ativo sem depender só do localStorage.
   * Cookie de sessão é compartilhado entre portas (4300/4200), mas localStorage não —
   * sem fallback, checkSession autentica e não carrega permissões → /access-denied.
   */
  private resolveSistemaAtivo(): SistemaEnum | null {
    if (this.appApiName === 'bardo') {
      return SistemaEnum.Bardo;
    }

    const fromStorage = this.getSistemaFromStorage();
    if (fromStorage) {
      return fromStorage;
    }

    // Apps Nomoh (admin/cliente) usam a mesma API; default seguro para carregar permissões
    if (this.appApiName === 'nomoh') {
      return SistemaEnum.Nomoh;
    }

    return null;
  }

  private syncToStorage(user: IUsuario | null, sistema?: SistemaEnum): void {
    if (user) {
      localStorage.setItem('nomoh_user', JSON.stringify(user));
      localStorage.setItem('nomoh_auth', 'true');
      if (sistema !== undefined) {
        localStorage.setItem('nomoh_sistema_id', sistema.toString());
      }
    } else {
      localStorage.removeItem('nomoh_user');
      localStorage.removeItem('nomoh_auth');
      localStorage.removeItem('nomoh_sistema_id');
    }
  }

  private setUser(user: IUsuario | null, sistema?: SistemaEnum) {
    this._user.set(user);
    this._isAuthenticated.set(!!user);
    this.syncToStorage(user, sistema); // Auto-sync localStorage
  }

  // Método para limpar auth (usado pelo interceptor)
  clearAuth(): void {
    this._sessionVerified.set(false);
    this.setUser(null);
    this._error.set(null);
    this._bardoContextoAtivo.set(null);
    this.permissionService.clearPermissions();
  }

  async login(request: LoginRequest): Promise<LoginResult> {
    this._loading.set(true);
    this._error.set(null);

    try {
      // Login usa a API do sistema pedido; sessão/refresh/OAuth continuam no baseUrl do app
      const loginBase = `${this.environment.getApiUrlWithEnum(
        request.sistema
      )}/auth`;
      const loginUrl = request.sistema === SistemaEnum.Bardo
        ? `${loginBase}/bardo/login`
        : `${loginBase}/login`;

      const resposta = await firstValueFrom(
        this.http.post<IUsuario | IBardoLoginMultiContexto>(loginUrl, request)
      );

      if ('multiContexto' in resposta && resposta.multiContexto) {
        if (resposta.contextos.length === 1) {
          const ok = await this.selecionarContexto(resposta.contextos[0]);
          return ok ? 'AUTENTICADO' : 'PLANO_BLOQUEADO';
        }
        this._pendingContextosBardo.set(resposta.contextos);
        return 'SELECIONAR_CONTEXTO';
      }

      const onboarding = resposta as IUsuario & {
        permissions?: string[];
      };
      const user = this.usuarioComPerfilCadastro(onboarding, onboarding);
      this.setUser(user, request.sistema);
      this._sessionVerified.set(true);

      if (this.redirecionarSePlanoBloqueado(user)) {
        return 'PLANO_BLOQUEADO';
      }

      if (request.sistema === SistemaEnum.Bardo) {
        // Login BARDO sem tela de contexto: dono recém-cadastrado que ainda não
        // criou nenhum estabelecimento (vai passar pelo wizard de primeiro acesso).
        this._bardoContextoAtivo.set({
          contextoId: user.id,
          tipo: 'DONO',
          nomeEstabelecimento: '',
          papel: 'ADMINISTRADOR',
        });
      }

      if (user.termoPendente && user.termos?.length) {
        this._termosPendentes.set(user.termos);
      }

      this.avaliarCompletarCadastro(user);

      this.pessoaDataService.buscarPessoaLogada();

      if (request.sistema === SistemaEnum.Bardo && onboarding.permissions?.length) {
        this.permissionService.setFromLoginResponse(SistemaEnum.Bardo, 'Bardo', onboarding.permissions);
      } else {
        await this.permissionService.loadPermissionsForSystem(request.sistema);
      }

      return 'AUTENTICADO';
    } catch (error: any) {
      this.setUser(null);
      const errorBody = error?.error as IAuthErrorResponse | undefined;

      if (this.navegarSemPlano(errorBody)) {
        return 'ERRO';
      }

      this._error.set(errorBody?.message || 'Email ou senha inválidos.');
      return 'ERRO';
    } finally {
      this._loading.set(false);
    }
  }

  async selecionarContexto(contexto: IContextoBardo): Promise<boolean> {
    this._loading.set(true);
    try {
      const result = await firstValueFrom(
        this.http.post<{
          id: string;
          nome?: string;
          name?: string;
          email: string | null;
          profissionalId?: string;
          quantidadeLogins?: number;
          perfilCadastroIncompleto?: boolean;
          sistemaId?: string;
          sistemaNome?: string;
          planoExpirado?: boolean;
          planoCancelado?: boolean;
          termoPendente?: boolean;
          termos?: ITermoPendente[];
          permissions?: string[];
        }>(`${this.baseUrl}/bardo/selecionar-contexto`, {
          contextoId: contexto.contextoId,
          tipo: contexto.tipo,
        })
      );
      const user = this.usuarioComPerfilCadastro(result, result);
      this._pendingContextosBardo.set([]);
      this.setUser(user, SistemaEnum.Bardo);
      this._sessionVerified.set(true);

      if (this.redirecionarSePlanoBloqueado(user)) {
        return false;
      }

      this._bardoContextoAtivo.set({
        ...contexto,
        profissionalId: result.profissionalId,
      });
      this.avaliarCompletarCadastro(user);

      if (user.termoPendente && user.termos?.length) {
        this._termosPendentes.set(user.termos);
      }

      this.pessoaDataService.buscarPessoaLogada();

      if (result.permissions?.length) {
        this.permissionService.setFromLoginResponse(SistemaEnum.Bardo, 'Bardo', result.permissions);
      } else {
        await this.permissionService.loadPermissionsForSystem(SistemaEnum.Bardo);
      }
      return true;
    } catch (error: any) {
      const errorBody = error?.error as IAuthErrorResponse | undefined;
      if (this.navegarSemPlano(errorBody)) {
        return false;
      }
      throw error;
    } finally {
      this._loading.set(false);
    }
  }

  async checkSession(): Promise<void> {
    this._loading.set(true);

    try {
      // ✅ appApiName é fixo por app (definido no main.ts, não depende de localStorage) —
      // é a forma confiável de saber "este app é o BARDO", inclusive na primeira visita
      // (ex.: SSO via "Acessar Sistema" no nomoh-cliente, quando ainda não existe
      // 'nomoh_sistema_id' salvo neste navegador para a origem do bardo-administrador).
      const isBardo = this.appApiName === 'bardo';

      if (isBardo) {
        // refreshToken já emite permissões no response e as aplica internamente.
        // Fallback: se a resposta não trouxe permissões (ex.: backend antigo em deploy
        // parcial), carrega via endpoint dedicado para não deixar o usuário sem acesso.
        const renovado = await this.refreshToken();
        if (!renovado) {
          throw { status: 401 };
        }
        if (this.redirecionarSePlanoBloqueado()) {
          this._sessionVerified.set(true);
          return;
        }
        this.pessoaDataService.buscarPessoaLogada();
        this._sessionVerified.set(true);
        if (!this.permissionService.$permissionsLoaded()) {
          await this.permissionService.loadPermissionsForSystem(SistemaEnum.Bardo);
        }
        void this.sincronizarPerfilCadastroPosSessaoBardo();
        return;
      }

      const sistema = this.resolveSistemaAtivo();
      const user = await firstValueFrom(
        this.http.get<IUsuario>(`${this.baseUrl}/me`)
      );
      this.pessoaDataService.buscarPessoaLogada();
      this.setUser(user, sistema ?? undefined);
      this._sessionVerified.set(true);
      if (this.redirecionarSePlanoBloqueado(user)) {
        return;
      }
      this.avaliarCompletarCadastro(user);

      // ✅ Sempre carregar permissões quando o sistema for resolvido
      if (sistema) {
        await this.permissionService.loadPermissionsForSystem(sistema);
      }
    } catch (error: any) {
      // 401 é esperado quando não há sessão ativa - não logar como erro
      if (error?.status !== 401) {
        console.error('Erro inesperado ao verificar sessão:', error);
      }
      this._sessionVerified.set(false);
      this.setUser(null);
    } finally {
      this._loading.set(false);
    }
  }

  async logout(): Promise<void> {
    this._loading.set(true);

    try {
      await firstValueFrom(this.http.get(`${this.baseUrl}/logout`));
    } finally {
      this._sessionVerified.set(false);
      this.setUser(null);
      this._completarCadastroPendente.set(false);
      this.pessoaDataService.clearUserInfo();
      this.permissionService.clearPermissions();
      this._loading.set(false);
      this.router.navigate([this.postLogoutRoute]);
    }
  }

  /**
   * Renova os tokens usando o refreshToken (cookie HttpOnly)
   * Chamado automaticamente pelo interceptor quando accessToken expira
   */
  async refreshToken(): Promise<boolean> {
    const sistema = this.resolveSistemaAtivo();
    const isBardo = sistema === SistemaEnum.Bardo;

    // BARDO tem seu próprio refresh: preserva (ou resolve de novo, se necessário)
    // o contexto estabelecimentoId/perfilAcesso. O genérico compartilhado por todos
    // os sistemas reemite um JWT "raso" e apagaria esse contexto a cada renovação.
    const url = isBardo
      ? `${this.baseUrl}/bardo/refresh-token`
      : `${this.baseUrl}/refresh-token`;

    try {
      const resposta = await firstValueFrom(
        this.http.get<
          IUsuario & {
            nome?: string;
            estabelecimentoId?: string;
            perfilAcesso?: PerfilAcessoBardo;
            tipo?: 'DONO' | 'PROFISSIONAL';
            profissionalId?: string;
            quantidadeLogins?: number;
            perfilCadastroIncompleto?: boolean;
            permissions?: string[];
          }
        >(url)
      );

      const user = this.usuarioComPerfilCadastro(resposta, resposta);

      this.setUser(user, sistema ?? undefined);

      if (this.redirecionarSePlanoBloqueado(user)) {
        return true;
      }

      this.avaliarCompletarCadastro(user);

      if (user.termoPendente && user.termos?.length) {
        this._termosPendentes.set(user.termos);
      }

      if (isBardo && resposta.permissions?.length) {
        this.permissionService.setFromLoginResponse(SistemaEnum.Bardo, 'Bardo', resposta.permissions);
      }

      if (isBardo && resposta.estabelecimentoId && resposta.tipo) {
        this._bardoContextoAtivo.set({
          contextoId: resposta.estabelecimentoId,
          tipo: resposta.tipo,
          nomeEstabelecimento: this._bardoContextoAtivo()?.nomeEstabelecimento ?? '',
          papel: resposta.perfilAcesso ?? 'ADMINISTRADOR',
          profissionalId: resposta.profissionalId,
        });
      } else if (isBardo) {
        // Dono recém-cadastrado que ainda não criou estabelecimento (mesmo caso
        // tratado em login(), linhas ~152-158): a resposta de onboarding não traz
        // estabelecimentoId/tipo, mas o contexto local precisa refletir DONO/ADMINISTRADOR
        // para o dashboard não cair no fallback de PROFISSIONAL durante o wizard.
        this._bardoContextoAtivo.set({
          contextoId: resposta.id,
          tipo: 'DONO',
          nomeEstabelecimento: this._bardoContextoAtivo()?.nomeEstabelecimento ?? '',
          papel: 'ADMINISTRADOR',
        });
      }

      return true;
    } catch (error: any) {
      const errorBody = error?.error as IAuthErrorResponse | undefined;
      if (this.navegarSemPlano(errorBody)) {
        return false;
      }
      console.error('❌ Falha ao renovar token:', error);
      console.error('Status:', error?.status, 'Mensagem:', error?.message);
      return false;
    }
  }

  confirmarConta(token: string, id: string): Observable<void> {
    return this.http.get<void>(
      `${this.environment.getApiUrl(
        'nomoh'
      )}/pessoa/confirmacaoEmail?token=${token}&id=${id}`
    );
  }

  private async sincronizarPerfilCadastroPosSessaoBardo(): Promise<void> {
    const user = this._user();
    if (
      user?.quantidadeLogins !== undefined &&
      user?.perfilCadastroIncompleto !== undefined
    ) {
      this.avaliarCompletarCadastro(user);
      return;
    }

    try {
      const perfil = await firstValueFrom(
        this.http.get<IUsuario>(`${this.baseUrl}/me`)
      );
      this.sincronizarCompletarCadastro(
        this.usuarioComPerfilCadastro(
          {
            id: perfil.id,
            email: perfil.email ?? user?.email ?? '',
            name: perfil.name ?? user?.name ?? '',
          },
          perfil
        )
      );
    } catch {
      // sessão inválida ou endpoint indisponível
    }
  }
}
